import { useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Skeleton } from "../components/ui/skeleton";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine } from "recharts";
import { Activity, RefreshCw } from "lucide-react";

export default function MetricsExplorerPage() {
  const [metric, setMetric] = useState("row_count");
  const [window, setWindow] = useState("24h");
  const [agg, setAgg] = useState("avg");
  const [data, setData] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchMetrics = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/metrics/query?metric=${metric}&window=${window}&agg=${agg}`);
      setData(d);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  }, [metric, window, agg]);

  useEffect(() => { fetchMetrics(); }, [fetchMetrics]);
  useEffect(() => { api.get("/metrics/catalog").then((r) => setCatalog(r.data)).catch(() => {}); }, []);

  const points = data?.points || [];
  const avg = points.length > 0 ? points.reduce((s, p) => s + p.value, 0) / points.length : 0;
  const max = points.length > 0 ? Math.max(...points.map((p) => p.value)) : 0;
  const min = points.length > 0 ? Math.min(...points.map((p) => p.value)) : 0;
  const currentMetric = catalog.find((c) => c.name === metric);

  const tt = { background: "#FFFFFF", border: "1px solid #E8E4DF", borderRadius: "8px", fontSize: "11px", fontFamily: "JetBrains Mono", color: "#C0CDD8" };

  return (
    <div className="p-6 space-y-5" data-testid="metrics-explorer-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[26px] font-extrabold tracking-tight text-[#2D3142]">Metrics Explorer</h1>
          <p className="text-[13px] mt-0.5" style={{ color: "#6B7280" }}>Query and visualize pipeline metrics</p>
        </div>
        <Button variant="outline" size="sm" className="rounded-lg border-gray-200 text-[#8899AA]" onClick={fetchMetrics}><RefreshCw className="w-3.5 h-3.5 mr-1.5" />Refresh</Button>
      </div>

      {/* Query builder */}
      <div className="sf-card p-4 flex flex-wrap items-end gap-3" data-testid="metrics-query-builder">
        <div className="space-y-1">
          <label className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: "#6B7280" }}>Metric</label>
          <Select value={metric} onValueChange={setMetric}>
            <SelectTrigger className="w-[180px] h-9 rounded-lg bg-black/[0.02] border-gray-200 text-[#2D3142]" data-testid="metric-select"><SelectValue /></SelectTrigger>
            <SelectContent style={{ background: "#FFFFFF", border: "1px solid #E8E4DF" }}>
              {catalog.map((m) => <SelectItem key={m.name} value={m.name}>{m.name.replace(/_/g, " ")}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <label className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: "#6B7280" }}>Aggregation</label>
          <Select value={agg} onValueChange={setAgg}>
            <SelectTrigger className="w-[100px] h-9 rounded-lg bg-black/[0.02] border-gray-200 text-[#2D3142]"><SelectValue /></SelectTrigger>
            <SelectContent style={{ background: "#FFFFFF" }}><SelectItem value="avg">avg</SelectItem><SelectItem value="sum">sum</SelectItem><SelectItem value="p95">p95</SelectItem><SelectItem value="p99">p99</SelectItem><SelectItem value="max">max</SelectItem><SelectItem value="min">min</SelectItem></SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <label className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: "#6B7280" }}>Window</label>
          <Select value={window} onValueChange={setWindow}>
            <SelectTrigger className="w-[100px] h-9 rounded-lg bg-black/[0.02] border-gray-200 text-[#2D3142]" data-testid="window-select"><SelectValue /></SelectTrigger>
            <SelectContent style={{ background: "#FFFFFF" }}><SelectItem value="1h">1 hour</SelectItem><SelectItem value="4h">4 hours</SelectItem><SelectItem value="24h">24 hours</SelectItem><SelectItem value="7d">7 days</SelectItem><SelectItem value="30d">30 days</SelectItem></SelectContent>
          </Select>
        </div>
        {currentMetric && <Badge className="text-[9px] font-mono border-0 rounded-md mb-0.5" style={{ background: "rgba(30,58,95,0.1)", color: "#1E3A5F" }}>{currentMetric.unit}</Badge>}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3">
        {[{ l: "Current", v: points[points.length - 1]?.value?.toFixed(2) || "—" }, { l: "Average", v: avg.toFixed(2) }, { l: "Maximum", v: max.toFixed(2) }, { l: "Minimum", v: min.toFixed(2) }].map((s) => (
          <div key={s.l} className="sf-card p-3">
            <div className="text-[9px] uppercase tracking-widest font-semibold mb-1" style={{ color: "#6B7280" }}>{s.l}</div>
            <div className="text-[20px] font-extrabold text-[#2D3142] font-mono">{s.v}</div>
          </div>
        ))}
      </div>

      {/* Chart */}
      <div className="sf-card p-5" data-testid="metrics-chart">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-[11px] uppercase tracking-[0.12em] font-semibold" style={{ color: "rgba(30,58,95,0.6)" }}>{metric.replace(/_/g, " ")} ({agg})</h3>
          <span className="text-[10px] font-mono" style={{ color: "#9CA3AF" }}>{points.length} data points</span>
        </div>
        {loading ? <Skeleton className="h-72 bg-white/5" /> : (
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={points}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E8E4DF" />
                <XAxis dataKey="timestamp" tick={{ fontSize: 9, fontFamily: "JetBrains Mono", fill: "#9CA3AF" }} tickFormatter={(v) => v?.slice(11, 16) || ""} stroke="#E8E4DF" />
                <YAxis tick={{ fontSize: 9, fontFamily: "JetBrains Mono", fill: "#9CA3AF" }} stroke="#E8E4DF" />
                <Tooltip contentStyle={tt} labelFormatter={(v) => v?.slice(0, 16)} />
                <ReferenceLine y={avg} stroke="rgba(30,58,95,0.3)" strokeDasharray="5 5" label={{ value: "avg", position: "right", fill: "#9CA3AF", fontSize: 9 }} />
                <Line type="monotone" dataKey="value" stroke="#29B5E8" strokeWidth={1.5} dot={false} activeDot={{ r: 4, fill: "#29B5E8", stroke: "#11192B", strokeWidth: 2 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}
